import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

// priceSnapshotId (TKT-433): price with a named price snapshot instead of
// today's live prices. null/'' = live prices.
const usePricing = (services, propertyInfo, markup = null, enabled = true, priceSnapshotId = null) => {
  const [pricing, setPricing] = useState({
    lawn: { seasonalTotal: 0, weeklyPrice: 0, baggingWeekly: 0, baggingTotal: 0, trashBagTotal: 0, enabled: false },
    gardening: { seasonalTotal: 0, weeklyTotal: 0, enabled: false },
    spring: { total: 0, enabled: false },
    fall: { total: 0, enabled: false },
    trimming: { total: 0, enabled: false },
    snow: { total: 0, enabled: false },
    turf: { total: 0, enabled: false },
    grandTotal: 0,
    timestamp: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Track previous valid pricing to prevent flashing to 0 during loading
  const [lastValidPricing, setLastValidPricing] = useState(null);

  // Requests can resolve out of order. Only the most recent one may update
  // state; otherwise a slow reply for an earlier form state overwrites the
  // current price and the screen sticks on a stale total.
  const latestRequestId = useRef(0);
  const inFlight = useRef(null);

  // Debounced calculation function
  const calculatePricing = useCallback(
    async (servicesData, propertyData, markupData, snapshotData) => {
      if (!enabled) return;

      const requestId = ++latestRequestId.current;
      if (inFlight.current) inFlight.current.abort();
      const controller = new AbortController();
      inFlight.current = controller;
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await axios.post('/api/pricing/calculate', {
          services: servicesData,
          propertyInfo: propertyData,
          markup: markupData,
          priceSnapshotId: snapshotData || null
        }, { signal: controller.signal });

        if (requestId !== latestRequestId.current) return;
        setPricing(response.data);
        setLastValidPricing(response.data);
      } catch (err) {
        if (axios.isCancel(err) || requestId !== latestRequestId.current) return;
        console.error('Pricing calculation error:', err);
        setError(err.response?.data?.error || 'Failed to calculate pricing');
        
        // Use last valid pricing if available instead of flashing to zero
        if (lastValidPricing) {
          setPricing(lastValidPricing);
        } else {
          // Fallback to zero values only if no previous valid data
          setPricing(prev => ({
            ...prev,
            grandTotal: 0
          }));
        }
      } finally {
        if (requestId === latestRequestId.current) {
          setLoading(false);
          inFlight.current = null;
        }
      }
    },
    [enabled, lastValidPricing]
  );

  // Debounce the pricing calculation to avoid too many API calls
  useEffect(() => {
    if (!enabled || !services) return;
    
    // Check if we have valid data to calculate
    const hasValidData = services && Object.keys(services).some(key => services[key]?.enabled);
    if (!hasValidData) {
      setLoading(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      calculatePricing(services, propertyInfo, markup, priceSnapshotId);
    }, 500); // Increased to 500ms debounce
    
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Use JSON.stringify for deep comparison of dependencies to avoid infinite loops
    // when objects are recreated with same values on re-renders
    JSON.stringify(services), 
    JSON.stringify(propertyInfo), 
    JSON.stringify(markup), 
    enabled,
    priceSnapshotId
  ]);

  useEffect(() => () => {
    if (inFlight.current) inFlight.current.abort();
  }, []);

  // Manual recalculate function for immediate updates
  const recalculate = useCallback(() => {
    if (services) {
      calculatePricing(services, propertyInfo, markup, priceSnapshotId);
    }
  }, [services, propertyInfo, markup, priceSnapshotId, calculatePricing]);

  return {
    pricing,
    loading,
    error,
    recalculate
  };
};

export default usePricing;
